import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, signOut } from 'aws-amplify/auth';

function Appeal() {
  const navigate = useNavigate();
  const [patientName, setPatientName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [requestedMedication, setRequestedMedication] = useState('');
  const [clinicalHistory, setClinicalHistory] = useState('');
  const [denialLetter, setDenialLetter] = useState('');
  const [denialExplanation, setDenialExplanation] = useState('');
  const [appealLetter, setAppealLetter] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [saved, setSaved] = useState(false);

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPdf(true);
    setPdfUploaded(false);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('https://clinixai.net/extract-pdf', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      setDenialLetter(data.text);
      setPdfUploaded(true);
    } catch (error) {
      alert('Error reading PDF. Please try pasting the text manually.');
    }
    setUploadingPdf(false);
  };

  const saveToHistory = async (explanation, letter) => {
    try {
      const user = await getCurrentUser();
      const response = await fetch('https://clinixai.net/save-appeal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: patientName,
          date_of_birth: dateOfBirth,
          diagnosis: diagnosis,
          requested_medication: requestedMedication,
          denial_letter: denialLetter,
          denial_explanation: explanation,
          appeal_letter: letter,
          clinical_history: clinicalHistory,
          user_id: user.userId,
        }),
      });
      const data = await response.json();
      if (data.id) {
        setCurrentRecordId(data.id);
      }
    } catch (error) {
      console.log('Save error:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setSaved(false);
    try {
      const response = await fetch('https://clinixai.net/generate-appeal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          denial_letter: denialLetter,
          patient_name: patientName,
          diagnosis: diagnosis,
          requested_medication: requestedMedication,
          clinical_history: clinicalHistory,
        }),
      });
      const data = await response.json();
      setDenialExplanation(data.denial_explanation);
      setAppealLetter(data.appeal_letter);
      await saveToHistory(data.denial_explanation, data.appeal_letter);
    } catch (error) {
      setDenialExplanation('Error generating appeal. Please try again.');
      setAppealLetter('');
    }
    setLoading(false);
  };

  const handleSaveChanges = async () => {
    if (!currentRecordId) return;
    try {
      await fetch(`https://clinixai.net/update-appeal/${currentRecordId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appeal_letter: appealLetter, status: 'pending' }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.log('Update error:', error);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      maxWidth: '900px',
      margin: '0 auto',
      padding: '20px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        borderBottom: '2px solid #1a365d',
        paddingBottom: '15px'
      }}>
        <h1 style={{ color: '#1a365d', margin: 0 }}>Clinix AI</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate('/appeal-history')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#edf2f7',
              color: '#1a365d',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Past Appeals
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e53e3e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <h2 style={{ color: '#2d3748', marginBottom: '8px' }}>
        PA Denial Appeal Generator
      </h2>
      <p style={{ color: '#718096', marginBottom: '24px' }}>
        Upload or paste the denial letter, add patient history, and we'll generate a targeted appeal letter.
      </p>

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#1a365d', marginBottom: '16px' }}>Patient Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Patient Name</label>
            <input
              type="text"
              placeholder="John Smith"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Date of Birth</label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Diagnosis</label>
            <input
              type="text"
              placeholder="e.g. Multifocal Motor Neuropathy (G61.82)"
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Requested Medication</label>
            <input
              type="text"
              placeholder="e.g. Gamunex-C"
              value={requestedMedication}
              onChange={(e) => setRequestedMedication(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568' }}>Patient Clinical History</label>
          <p style={{ color: '#718096', fontSize: '12px', marginBottom: '8px' }}>
            Include how long on medication, recent evaluation dates, objective scale scores, and any relevant clinical notes
          </p>
          <textarea
            placeholder="e.g. Patient has been on IVIG since 2021. Last evaluated January 29, 2026 showing continued improvement..."
            value={clinicalHistory}
            onChange={(e) => setClinicalHistory(e.target.value)}
            rows={5}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'Arial, sans-serif' }}
          />
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#1a365d', marginBottom: '8px' }}>Denial Letter</h3>
        <div style={{
          border: '2px dashed #cbd5e0',
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center',
          marginBottom: '16px',
          backgroundColor: '#f7fafc'
        }}>
          <p style={{ color: '#718096', marginBottom: '12px', fontSize: '14px' }}>Upload denial letter as PDF</p>
          <label style={{ padding: '8px 20px', backgroundColor: '#1a365d', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
            {uploadingPdf ? 'Reading PDF...' : 'Upload PDF'}
            <input type="file" accept=".pdf" onChange={handlePdfUpload} style={{ display: 'none' }} />
          </label>
          {pdfUploaded && !uploadingPdf && (
            <div>
              <p style={{ color: '#48bb78', fontSize: '13px', marginTop: '8px' }}>✓ PDF loaded successfully</p>
              <button
                onClick={() => {
                  setPdfUploaded(false);
                  setDenialLetter('');
                }}
                style={{
                  marginTop: '8px',
                  padding: '4px 10px',
                  backgroundColor: 'transparent',
                  color: '#e53e3e',
                  border: '1px solid #e53e3e',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Remove PDF
              </button>
            </div>
          )}
        </div>

        {!pdfUploaded && (
          <>
            <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px', textAlign: 'center' }}>— or paste the text manually —</p>
            <textarea
              placeholder="Paste the full denial letter text here..."
              value={denialLetter}
              onChange={(e) => setDenialLetter(e.target.value)}
              rows={8}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'Arial, sans-serif' }}
            />
          </>
        )}
      </div>

      <button
        onClick={handleGenerate}
        disabled={loading || !denialLetter || !patientName}
        style={{
          width: '100%',
          padding: '14px',
          backgroundColor: loading || !denialLetter || !patientName ? '#a0aec0' : '#1a365d',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '16px',
          cursor: loading || !denialLetter || !patientName ? 'not-allowed' : 'pointer',
          marginBottom: '20px'
        }}
      >
        {loading ? 'Generating Appeal...' : 'Generate Appeal Letter'}
      </button>

      {denialExplanation && (
        <div style={{
          backgroundColor: '#fff5f5',
          border: '1px solid #fed7d7',
          padding: '24px',
          borderRadius: '10px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#c53030', marginBottom: '12px' }}>Why It Was Denied — Plain English</h3>
          <p style={{ color: '#2d3748', lineHeight: '1.8' }}>{denialExplanation}</p>
        </div>
      )}

      {appealLetter && (
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: '#1a365d', margin: 0 }}>Generated Appeal Letter</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {saved && <span style={{ color: '#48bb78', fontSize: '13px' }}>Saved</span>}
              <button
                onClick={handleSaveChanges}
                style={{ padding: '8px 16px', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Save Changes
              </button>
              <button
                onClick={() => navigator.clipboard.writeText(appealLetter)}
                style={{ padding: '8px 16px', backgroundColor: '#48bb78', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Copy
              </button>
            </div>
          </div>
          <textarea
            value={appealLetter}
            onChange={(e) => setAppealLetter(e.target.value)}
            rows={16}
            style={{ width: '100%', padding: '14px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', lineHeight: '1.8', color: '#2d3748', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'Arial, sans-serif' }}
          />
        </div>
      )}
    </div>
  );
}

export default Appeal;
